// ./src/components/features/EventVendors/VendorRequirementsList/VendorRequirementsList.tsx
//
// ROADMAP #170 — the documents an event asks of one vendor: a table on desktop, cards below (buttons on a tablet, a swipe
// on a phone), like every list (owner, 2026-10-20). Each row: what is asked and where it stands (its badges), by when, when
// what they sent expires, and the note when it was sent back. Actions: Review (what was sent) — or Find one (nothing
// sent yet: use one on file, or upload one they sent you) — Edit the request, Stop asking.
// ⓘ The whole set is shown (a vendor owes a handful; the event vendor carries them all), so there is no Load more.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Skeleton, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EventVendorRequirement, VENDOR_REQUIREMENT_STATUS } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";

import { RequirementBadges } from "../RequirementBadges";
import { formatVendorDay } from "../vendorUi";

export interface VendorRequirementsListProps {
  data: EventVendorRequirement[];
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  /** True when a search is narrowing the list — then an empty one is "no match", not "none yet". */
  hasQueryNarrowing?: boolean;
  /** Review what was sent, or find one when nothing was. Absent = read only. */
  onReview?: (r: EventVendorRequirement) => void;
  onEdit?: (r: EventVendorRequirement) => void;
  onDelete?: (r: EventVendorRequirement) => void;
  isRemoving: (id?: string) => boolean;
  /** First-run empty state's action. */
  onAdd?: () => void;
}

interface RowProps {
  r: EventVendorRequirement;
  onReview?: (r: EventVendorRequirement) => void;
  onEdit?: (r: EventVendorRequirement) => void;
  onDelete?: (r: EventVendorRequirement) => void;
  isDeleting: boolean;
}

const NameCell: React.FC<{ r: EventVendorRequirement }> = ({ r }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Stack gap={4} style={{ minWidth: 0 }}>
      <Text size="md" fw={600}>
        {r.name}
      </Text>
      <RequirementBadges requirement={r} />
      {r.venueName ? (
        <Text size="md" c="dimmed">
          {t("vendors:requirement.askedBy", { defaultValue: "Asked by {{venue}}", venue: r.venueName })}
        </Text>
      ) : null}
      {r.reviewStatus === VENDOR_REQUIREMENT_STATUS.RETURNED && r.reviewNote ? (
        <Text size="md" c="orange" lineClamp={2}>
          {t("vendors:requirement.sentBack", { defaultValue: "Sent back: {{note}}", note: r.reviewNote })}
        </Text>
      ) : null}
    </Stack>
  );
};

const DueText: React.FC<{ r: EventVendorRequirement; withLabel: boolean }> = ({ r, withLabel }) => {
  const { t } = useTranslation(["vendors"]);

  if (!r.dueDate) {
    return withLabel ? null : (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }

  const day = formatVendorDay(r.dueDate);

  return (
    <Text size="md" c={r.attention === "overdue" ? "red" : "dimmed"}>
      {withLabel ? t("vendors:requirement.neededBy", { defaultValue: "Needed by {{date}}", date: day }) : day}
    </Text>
  );
};

const ExpiresText: React.FC<{ r: EventVendorRequirement; withLabel: boolean }> = ({ r, withLabel }) => {
  const { t } = useTranslation(["vendors"]);

  if (!r.document?.expiresOn) {
    return withLabel ? null : (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }

  const day = formatVendorDay(r.document.expiresOn);

  return (
    <Text size="md" c={r.document.expiresBeforeEvent ? "red" : "dimmed"}>
      {withLabel ? t("vendors:requirement.expires", { defaultValue: "Expires {{date}}", date: day }) : day}
    </Text>
  );
};

const RowActions: React.FC<RowProps> = ({ r, onReview, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["vendors"]);
  const reviewLabel = r.document ? t("vendors:requirement.review", { defaultValue: "Review" }) : t("vendors:requirement.findOne", { defaultValue: "Find one" });

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {onReview ? (
        <Tooltip label={reviewLabel} withArrow>
          <ActionIcon
            variant={r.reviewStatus === VENDOR_REQUIREMENT_STATUS.SUBMITTED ? "filled" : "subtle"}
            color={r.reviewStatus === VENDOR_REQUIREMENT_STATUS.SUBMITTED ? "blue" : "neutral"}
            size="lg"
            disabled={isDeleting}
            onClick={() => onReview(r)}
            aria-label={reviewLabel}
          >
            <AppIcon iconName={r.document ? "ClipboardCheck" : "Search"} size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onEdit ? (
        <Tooltip label={t("vendors:requirement.edit", { defaultValue: "Edit the request" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="neutral"
            size="lg"
            disabled={isDeleting}
            onClick={() => onEdit(r)}
            aria-label={t("vendors:requirement.edit", { defaultValue: "Edit the request" })}
          >
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete ? (
        <Tooltip label={t("vendors:requirement.stop", { defaultValue: "Stop asking for it" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            onClick={() => onDelete(r)}
            aria-label={t("vendors:requirement.stop", { defaultValue: "Stop asking for it" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const RequirementCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["vendors"]);
  const { r } = p;
  const hasActions = !!p.onReview || !!p.onEdit || !!p.onDelete;

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (p.onReview) {
      actions.push({
        key: "review",
        icon: r.document ? "ClipboardCheck" : "Search",
        label: r.document ? t("vendors:requirement.review", { defaultValue: "Review" }) : t("vendors:requirement.findOne", { defaultValue: "Find one" }),
        color: "blue",
        onClick: () => p.onReview?.(r),
        disabled: p.isDeleting,
        // A "do something" action, not edit / remove — the other side of the card.
        secondary: true,
      });
    }

    if (p.onEdit) {
      actions.push({
        key: "edit",
        icon: "Pencil",
        label: t("vendors:requirement.editShort", { defaultValue: "Edit" }),
        color: "gray",
        onClick: () => p.onEdit?.(r),
        disabled: p.isDeleting,
      });
    }

    if (p.onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("vendors:requirement.stopShort", { defaultValue: "Stop" }),
        color: "red",
        onClick: () => p.onDelete?.(r),
        loading: p.isDeleting,
        disabled: p.isDeleting,
      });
    }

    return actions;
  }, [p, r, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={p.isDeleting ? 0.5 : 1} style={{ transition: "opacity 150ms ease" }}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          <NameCell r={r} />
          <DueText r={r} withLabel />
          <ExpiresText r={r} withLabel />
        </Stack>
        {showActions && hasActions ? <RowActions {...p} /> : null}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const RequirementCard = React.memo(RequirementCardComponent);

const VendorRequirementsListComponent: React.FC<VendorRequirementsListProps> = ({
  data,
  isLoaded,
  hasQueryNarrowing = false,
  onReview,
  onEdit,
  onDelete,
  isRemoving,
  onAdd,
}) => {
  const { t } = useTranslation(["vendors"]);
  const { isDesktop } = useAppEnvironment();
  // The tablet card's row buttons (Review + Edit + Stop asking) — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onReview, onEdit, onDelete);
  const withActions = cardActions > 0;
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleReview = useCallback(
    (r: EventVendorRequirement) => {
      closeAllCards();
      onReview?.(r);
    },
    [onReview, closeAllCards]
  );

  const handleEdit = useCallback(
    (r: EventVendorRequirement) => {
      closeAllCards();
      onEdit?.(r);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (r: EventVendorRequirement) => {
      closeAllCards();
      onDelete?.(r);
    },
    [onDelete, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("vendors:requirement.columns.document", { defaultValue: "Document" })}</Table.Th>
        <Table.Th style={{ width: 150 }}>{t("vendors:requirement.columns.due", { defaultValue: "Needed by" })}</Table.Th>
        <Table.Th style={{ width: 150 }}>{t("vendors:requirement.columns.expires", { defaultValue: "Expires" })}</Table.Th>
        {withActions ? <Table.Th style={{ width: 140 }} /> : null}
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width="55%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    {withActions ? (
                      <Table.Td>
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                          {Array.from({ length: cardActions }).map((_, a) => (
                            <Skeleton key={a} circle height={34} width={34} />
                          ))}
                        </Group>
                      </Table.Td>
                    ) : null}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: what is asked, its badges, by when.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={3} sizes={["md", "sm", "md"]} gap={6} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between" align="flex-start">
              <CardSkeletonLines count={3} sizes={["md", "sm", "md"]} gap={6} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vendors:requirements.noMatch", { defaultValue: "No requests match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("vendors:requirement.emptyTitle", { defaultValue: "Nothing asked for yet" })}
          description={t("vendors:requirement.emptyBody", {
            defaultValue: "Ask for each document this vendor owes — a certificate of insurance, a food permit — then email them their link (the Link tab).",
          })}
          iconName="FileCertificate"
          actionLabel={onAdd ? t("vendors:requirement.add", { defaultValue: "Ask for a document" }) : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (r: EventVendorRequirement): RowProps => ({
    r,
    onReview: onReview ? handleReview : undefined,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    isDeleting: isRemoving(r.id),
  });

  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          {header}
          <Table.Tbody>
            {data.map((r) => {
              const p = rowProps(r);

              return (
                <Table.Tr key={r.id} style={{ verticalAlign: "top", opacity: p.isDeleting ? 0.5 : 1, transition: "opacity 150ms ease" }}>
                  <Table.Td>
                    <NameCell r={r} />
                  </Table.Td>
                  <Table.Td>
                    <DueText r={r} withLabel={false} />
                  </Table.Td>
                  <Table.Td>
                    <ExpiresText r={r} withLabel={false} />
                  </Table.Td>
                  {withActions ? (
                    <Table.Td>
                      <RowActions {...p} />
                    </Table.Td>
                  ) : null}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {data.map((r) => (
        <RequirementCard
          key={r.id}
          {...rowProps(r)}
          forceClose={shouldForceClose(r.id || "")}
          onForceCloseComplete={() => handleForceCloseComplete(r.id || "")}
          onSwipeStateChange={(isOpen) => isOpen && openCard(r.id || "")}
        />
      ))}
    </Stack>
  );
};

export const VendorRequirementsList = React.memo(VendorRequirementsListComponent);
